/**
 * Real AWS Cognito Authentication Service
 * Replaces mock authentication with actual Cognito integration
 */

import { Amplify } from 'aws-amplify';
// REQUIRED for Google SSO: registers the side-effect listener that completes
// the OAuth flow (exchanges the ?code= in the URL for tokens) when the
// browser returns from the redirect. Without this import, sign-in silently
// never completes and the user bounces back to the login screen.
import 'aws-amplify/auth/enable-oauth-listener';
import { hipaaService } from './hipaaComplianceService';
import {
  signUp,
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  fetchAuthSession,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
  signInWithRedirect,
  autoSignIn,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference
} from 'aws-amplify/auth';

// Configure Amplify directly with correct v6 structure
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: 'us-east-2_KB0wnBXtR',
      userPoolClientId: '2a403j4oul0najb9dflitedcrp',
      region: 'us-east-2',
      loginWith: {
        oauth: {
          domain: 'chartara-notes-auth.auth.us-east-2.amazoncognito.com',
          scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
          redirectSignIn: ['https://notes.chartaralabs.io/', 'http://localhost:3000/'],
          redirectSignOut: ['https://notes.chartaralabs.io/', 'http://localhost:3000/'],
          responseType: 'code'
        }
      }
    }
  }
});

class CognitoAuthService {
  constructor() {
    this.currentUser = null;
    this.initializeFromSession();
  }

  /**
   * Initialize authentication from existing session
   */
  async initializeFromSession() {
    try {
      const user = await getCurrentUser();
      if (user) {
        // Fetch attributes explicitly (Amplify v6) — required for federated
        // users (Google), whose username is provider-prefixed, not the email.
        let attributes = {};
        try { attributes = await fetchUserAttributes(); } catch {}
        this.currentUser = this.buildUserObject(user, attributes);
        try { localStorage.setItem('currentUser', JSON.stringify(this.currentUser)); } catch {}
      }
    } catch (error) {
      console.log('No existing session found');
      this.currentUser = null;
      try { localStorage.removeItem('currentUser'); } catch {}
    }
  }

  /**
   * Build the app's user object from Cognito user + attributes.
   * Works for native users (username = email) and federated users
   * (username = "google_..."; email lives in attributes).
   */
  buildUserObject(user, attributes = {}) {
    const email = (attributes?.email || user.username || '').toLowerCase();
    return {
      id: user.userId,
      email: email,
      name: attributes?.given_name || attributes?.name || email,
      role: attributes?.['custom:role'] || 'therapist',
      license: attributes?.['custom:license'] || '',
      practice: attributes?.['custom:practice'] || ''
    };
  }

  /**
   * Start Google SSO — redirects the browser to Google via the Cognito
   * Hosted UI. On return, App.jsx's Hub listener completes the session.
   */
  async signInWithGoogle() {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * "Email me a code" — passwordless sign-in/sign-up with a one-time code.
   * Works for ANY email address. New users are created automatically
   * (no password ever); existing passwordless/password users get a
   * sign-in code via Cognito's native EMAIL_OTP factor.
   * @param {string} rawEmail
   * @returns {{flow: 'signup'|'signin'}} which verify path to use
   */
  async requestEmailCode(rawEmail) {
    const email = (rawEmail || '').trim().toLowerCase();
    if (!email) throw new Error('Please enter your email address.');
    try {
      // Try to create the account first (passwordless). If it already
      // exists Cognito tells us and we fall through to sign-in. This
      // order avoids user-enumeration issues on the sign-in path.
      // NOTE: pool schema has NO custom:role attribute (CLAUDE.md is wrong
      // about that) — the app defaults role to 'therapist' at read time.
      await signUp({
        username: email,
        options: {
          userAttributes: { email: email },
          autoSignIn: { authFlowType: 'USER_AUTH' }
        }
      });
      return { flow: 'signup', email };
    } catch (error) {
      const code = error.name || error.code;
      if (code === 'UsernameExistsException') {
        // Existing account — request a one-time sign-in code
        const result = await signIn({
          username: email,
          options: {
            authFlowType: 'USER_AUTH',
            preferredChallenge: 'EMAIL_OTP'
          }
        });
        const step = result.nextStep?.signInStep;
        if (step === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
          return { flow: 'signin', email };
        }
        throw new Error('This account requires password sign-in. Please use your password below.');
      }
      console.error('Email code request error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Complete the email-code flow with the 6-digit code the user received.
   * @param {string} code - the emailed one-time code
   * @param {'signup'|'signin'} flow - from requestEmailCode
   * @param {string} email
   * @returns {Object} the signed-in user object
   */
  async verifyEmailCode(code, flow, email) {
    try {
      if (flow === 'signup') {
        await confirmSignUp({
          username: email,
          confirmationCode: code.trim()
        });
        // Complete the passwordless auto sign-in started at signUp
        const { isSignedIn } = await autoSignIn();
        if (!isSignedIn) throw new Error('Sign-in could not be completed. Please try again.');
      } else {
        const result = await confirmSignIn({ challengeResponse: code.trim() });
        if (!result.isSignedIn) throw new Error('Invalid code. Please try again.');
      }

      const user = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      this.currentUser = this.buildUserObject(user, attributes);
      try { localStorage.setItem('currentUser', JSON.stringify(this.currentUser)); } catch {}
      return this.currentUser;
    } catch (error) {
      console.error('Email code verify error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Sign up a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.name - User's full name
   * @param {string} userData.license - Professional license number
   * @param {string} userData.practice - Practice name
   * @returns {Object} Sign up result
   */
  async signUp(userData) {
    try {
      const { email, password, name, license, practice } = userData;
      
      const result = await signUp({
        username: email,
        password: password,
        attributes: {
          email: email,
          name: name,
          'custom:role': 'therapist',
          'custom:license': license || '',
          'custom:practice': practice || ''
        }
      });

      return {
        success: true,
        userId: result.userId,
        isSignUpComplete: result.isSignUpComplete,
        nextStep: result.nextStep
      };
    } catch (error) {
      console.error('Sign up error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Confirm sign up with verification code
   * @param {string} email - User email
   * @param {string} confirmationCode - Verification code
   * @returns {Object} Confirmation result
   */
  async confirmSignUp(email, confirmationCode) {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: confirmationCode
      });

      return { success: true };
    } catch (error) {
      console.error('Confirm sign up error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Resend confirmation code
   * @param {string} email - User email
   * @returns {Object} Resend result
   */
  async resendConfirmationCode(email) {
    try {
      await resendSignUpCode({
        username: email
      });

      return { success: true };
    } catch (error) {
      console.error('Resend confirmation code error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * User sign in
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Object} User object
   */
  async login(credentials) {
    try {
      const { email, password } = credentials;
      
      const result = await signIn({
        username: email,
        password: password
      });

      // Debug logging only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEV] Sign in result:', result);
      }

      if (result.isSignedIn) {
        // Get user details and attributes separately in Amplify v6
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        // Debug logging only in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEV] Current user from Cognito:', user);
          console.log('[DEV] User attributes:', attributes);
        }
        
        this.currentUser = this.buildUserObject(user, attributes);
        try { localStorage.setItem('currentUser', JSON.stringify(this.currentUser)); } catch {}

        // Debug logging only in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEV] Processed user object:', hipaaService.sanitizeForLogging(this.currentUser));
        }
        return this.currentUser;
      } else {
        // Handle different sign-in states
        const nextStep = result.nextStep;
        // Debug logging only in development
        if (process.env.NODE_ENV === 'development') {
          console.log('[DEV] Next step required:', nextStep);
        }
        
        if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          // Debug logging only in development
          if (process.env.NODE_ENV === 'development') {
            console.log('[DEV] Missing attributes:', nextStep.missingAttributes);
          }
          // Return special object indicating password change needed
          return {
            needsPasswordChange: true,
            challengeResult: result,
            email: email,
            missingAttributes: nextStep.missingAttributes || []
          };
        } else {
          throw new Error(`Sign in not completed. Next step: ${nextStep?.signInStep || 'Unknown'}`);
        }
      }
    } catch (error) {
      // HIPAA Compliance: Log failed login attempts
      hipaaService.logAuthEvent('FAILED_LOGIN', email);
      
      console.error('Login error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Complete password change for forced password change flow
   * @param {Object} challengeResult - The result from initial signIn
   * @param {string} newPassword - The new password
   * @returns {Object} User object
   */
  async completePasswordChange(challengeResult, newPassword, email = '') {
    try {
      // Debug logging only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[DEV] Attempting password change for email:', hipaaService.maskEmail(email));
      }
      
      // Handle missing attributes - in Amplify v6, they need to be provided as options
      let confirmSignInOptions = {
        challengeResponse: newPassword
      };
      
      if (challengeResult.missingAttributes && challengeResult.missingAttributes.length > 0 && email) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Providing missing attributes:', challengeResult.missingAttributes);
          console.log('Email to provide:', email);
        }
        confirmSignInOptions.userAttributes = {
          email: email
        };
        if (process.env.NODE_ENV === 'development') {
          console.log('Created userAttributes object:', confirmSignInOptions.userAttributes);
        }
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('confirmSignIn options:', confirmSignInOptions);
      }
      
      // Try different parameter structure for Amplify v6
      let result;
      if (confirmSignInOptions.userAttributes) {
        if (process.env.NODE_ENV === 'development') {
          console.log('Calling confirmSignIn with separate userAttributes');
        }
        result = await confirmSignIn({
          challengeResponse: newPassword,
          userAttributes: confirmSignInOptions.userAttributes
        });
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('Calling confirmSignIn without userAttributes');
        }
        result = await confirmSignIn({
          challengeResponse: newPassword
        });
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('Password change result:', result);
      }

      if (result.isSignedIn) {
        // Get user details and attributes separately in Amplify v6
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('Password change - Current user from Cognito:', user);
          console.log('Password change - User attributes from fetchUserAttributes:', attributes);
        }
        
        this.currentUser = this.buildUserObject(user, attributes);
        try { localStorage.setItem('currentUser', JSON.stringify(this.currentUser)); } catch {}

        if (process.env.NODE_ENV === 'development') {
          console.log('Password change - Processed user object:', hipaaService.sanitizeForLogging(this.currentUser));
        }
        return this.currentUser;
      } else {
        throw new Error('Password change not completed');
      }
    } catch (error) {
      console.error('Password change error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * User sign out
   */
  async logout() {
    try {
      const currentEmail = this.currentUser?.email;
      
      await signOut();
      
      // HIPAA Compliance: Log logout and clear session
      if (currentEmail) {
        hipaaService.logAuthEvent('LOGOUT', currentEmail);
      }
      hipaaService.clearSession();

      this.currentUser = null;
      try { localStorage.removeItem('currentUser'); } catch {}
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  /**
   * Get current authenticated user
   * @returns {Object|null} Current user or null
   */
  getCurrentUser() {
    return this.currentUser;
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!this.currentUser;
  }

  /**
   * Get authentication token
   * @returns {string|null} JWT token
   */
  async getToken() {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get token:', error);
      return null;
    }
  }

  /**
   * Verify current token
   * @returns {boolean} Token validity status
   */
  async verifyToken() {
    try {
      const session = await fetchAuthSession();
      return !!session.tokens?.accessToken;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initiate forgot password flow
   * @param {string} email - User email
   * @returns {Object} Reset password result
   */
  async forgotPassword(email) {
    try {
      const result = await resetPassword({
        username: email
      });

      return {
        success: true,
        nextStep: result.nextStep
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Confirm password reset with code
   * @param {string} email - User email
   * @param {string} confirmationCode - Reset code
   * @param {string} newPassword - New password
   * @returns {Object} Confirmation result
   */
  async confirmResetPassword(email, confirmationCode, newPassword) {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: confirmationCode,
        newPassword: newPassword
      });

      return { success: true };
    } catch (error) {
      console.error('Confirm reset password error:', error);
      throw new Error(this.getErrorMessage(error));
    }
  }

  /**
   * Create authenticated request headers for API calls
   * @returns {Object} Headers object with authentication
   */
  async getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = await this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Get user-friendly error message
   * @param {Error} error - AWS Amplify error
   * @returns {string} User-friendly error message
   */
  getErrorMessage(error) {
    const errorCode = error.name || error.code;
    
    switch (errorCode) {
      case 'UserNotConfirmedException':
        return 'Please check your email and confirm your account before signing in.';
      case 'NotAuthorizedException':
        return 'Invalid email or password. Please try again.';
      case 'UserNotFoundException':
        return 'No account found with this email address.';
      case 'InvalidPasswordException':
        return 'Password must be at least 8 characters long and contain uppercase, lowercase, and numbers.';
      case 'UsernameExistsException':
        return 'An account with this email already exists.';
      case 'CodeMismatchException':
        return 'Invalid verification code. Please try again.';
      case 'ExpiredCodeException':
        return 'Verification code has expired. Please request a new one.';
      case 'LimitExceededException':
        return 'Too many attempts. Please try again later.';
      case 'NetworkError':
        return 'Network error. Please check your connection and try again.';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  }
}

// Create and export singleton instance
export const cognitoAuthService = new CognitoAuthService();
export default cognitoAuthService;